import {
	decodeBytes,
	ECDH_KEY_SPEC,
	encodeBytes,
} from "open-signer-shared-cryptography";
import { z } from "zod/v4";
import type { CrossmintApiService } from "../api";
import { measureFunctionTime } from "../common/utils";
import type { FrameLogger } from "../logger";
import { CrossmintFrameService } from "../service";
import type { AttestationService } from "./attestation";
import type { AttestationBundle } from "./types";
import { DstackVerifier } from "./verifier/dstack";

const TEE_REPORT_DATA_PREFIX = "app-data:";
const TEE_REPORT_DATA_HASH = "SHA-512" as const;
const TEE_REPORT_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

// RTMR calculation constants - Based on DStack TEE implementations
const INIT_MR =
	"000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000";

// Event log filtering constants for RTMR3
const RTMR3_IMR_INDEX = 3;
const RTMR3_HASH_ALGORITHM = "SHA-384";
const SHA512_HASH_LENGTH = 64;
const SHA384_HASH_LENGTH = 48;

// Event name mappings for application info extraction
const EVENT_NAMES = {
	APP_ID: "app-id",
	COMPOSE_HASH: "compose-hash",
	INSTANCE_ID: "instance-id",
	KEY_PROVIDER: "key-provider",
} as const;

// Event log schemas
const EventLogEntrySchema = z.object({
	imr: z.number(),
	event_type: z.number(),
	digest: z.string(),
	event: z.string(),
	event_payload: z.string(),
});

const EventLogSchema = z.array(EventLogEntrySchema);

const KeyProviderSchema = z.object({
	name: z.literal("kms"),
	id: z.string(),
});

const ApplicationInfoSchema = z.object({
	app_id: z.string(),
	compose_hash: z.string(),
	instance_id: z.string(),
	key_provider: KeyProviderSchema,
});

type EventLogEntry = z.infer<typeof EventLogEntrySchema>;
type ApplicationInfo = z.infer<typeof ApplicationInfoSchema>;

const MAX_RETRIES = 5;
const INITIAL_RETRY_INTERVAL_MS = 1000;
const BACKOFF_COEFFICIENT = 1.41;

/**
 * TEE Attestation Service for DStack Applications
 *
 * This service performs remote attestation for DStack TEE applications by:
 * 1. Verifying Intel TDX quotes using Phala's DCAP QVL library
 * 2. Validating application integrity through RTMR3 measurement replay
 * 3. Ensuring cryptographic commitment to the TEE's public key
 *
 * By doing so it ensures this iframe only interacts with and encrypts
 * sensitive data to a TEE running the authorized application code that
 * completes this system.
 *
 * The implementation is based on the reference attestation verification code
 * provided by the Phala team for DStack applications:
 * https://github.com/Dstack-TEE/dstack-examples/blob/main/attestation/rtmr3-based/verify.py
 *
 */
export class TEEAttestationService
	extends CrossmintFrameService
	implements AttestationService
{
	name = "DStack Attestation Service";
	log_prefix = "[TEEAttestationService]";

	private readonly expectedAppIds: string[];

	constructor(
		private readonly api: CrossmintApiService,
		expectedAppId: string | string[],
		protected readonly logger: FrameLogger,
		private readonly dstackVerifier = new DstackVerifier(logger),
	) {
		super(logger);
		this.expectedAppIds = Array.isArray(expectedAppId)
			? expectedAppId
			: [expectedAppId];
	}

	// This being not null implicitly assumes validation
	private publicKey: CryptoKey | null = null;

	async init(retryCount = 0) {
		try {
			this.logger.debug(
				this.log_prefix,
				`Fetching the attestation from the TEE...`,
			);
			const { time: timeAttestationFetch, result: bundle } =
				await measureFunctionTime(() => this.api.getAttestation());
			this.logger.info(
				`${this.log_prefix} TEE attestation document fetched in ${timeAttestationFetch}ms`,
			);

			this.logger.debug(this.log_prefix, `Verifying attestation`);
			const {
				time: attestationVerificationTime,
				result: { reportData, rtMr3 },
			} = await measureFunctionTime(() =>
				this.verifyTEEReportAndExtractTD(bundle),
			);
			this.logger.info(
				this.log_prefix,
				`Attestation verifed in ${attestationVerificationTime}ms`,
			);

			this.logger.info(
				`${this.log_prefix} Verifying TEE application integrity`,
			);
			await this.verifyTEEApplicationIntegrity(bundle.event_log, rtMr3);

			this.logger.info(
				`${this.log_prefix} Verifying relay reported public key`,
			);
			await this.verifyTEEPublicKey(
				reportData,
				bundle.publicKey,
				bundle.timestamp,
			);

			const publicKeyBytes = decodeBytes(bundle.publicKey, "base64");
			this.publicKey = await crypto.subtle.importKey(
				"raw",
				new Uint8Array(publicKeyBytes),
				ECDH_KEY_SPEC,
				true,
				[],
			);

			this.logger.debug(this.log_prefix, "TEE attestation verified");
		} catch (e: unknown) {
			if (retryCount >= MAX_RETRIES) {
				const errorMessage = `Failed to validate attestation document after ${MAX_RETRIES} retries.`;
				this.logger.error(this.log_prefix, errorMessage, e);
				this.publicKey = null;
				throw e instanceof Error ? e : new Error(errorMessage);
			}

			const delay =
				INITIAL_RETRY_INTERVAL_MS * Math.pow(BACKOFF_COEFFICIENT, retryCount);
			this.logger.warn(
				this.log_prefix,
				`Failed to validate attestation document. Retrying ${retryCount + 1}/${MAX_RETRIES} in ${Math.round(delay)}ms...`,
				e,
			);

			this.publicKey = null;
			await new Promise((resolve) => setTimeout(resolve, delay));
			await this.init(retryCount + 1);
		}
	}

	async verifyTEEReportAndExtractTD(
		bundle: AttestationBundle,
	): Promise<{ reportData: string; rtMr3: string }> {
		this.logger.info(`${this.log_prefix} Verifying intel TDX quote`);

		// The quote is verified locally only (DCAP signature chain against the
		// Intel root, signed TCB info and the 'UpToDate' TCB policy). A
		// rejection here is final: there is deliberately no fallback to a remote
		// verifier, because the relay controls the quote and collateral and could
		// otherwise force verification onto a weaker path that does not enforce
		// the same TCB policy or trust anchor.
		try {
			const { time, result } = await measureFunctionTime(() =>
				this.dstackVerifier.verifyTEEReportAndExtractTD(bundle),
			);
			this.logger.info(
				`Verification with ${this.dstackVerifier.name} verifier completed in ${time}ms`,
			);
			return result;
		} catch (error) {
			this.publicKey = null;
			const errorMessage = `Failed to verify TEE quote: ${error instanceof Error ? error.message : "Unknown error"}`;
			this.logger.error(`${this.log_prefix} ${errorMessage}`);
			throw new Error(errorMessage);
		}
	}

	async getAttestedPublicKey(): Promise<CryptoKey> {
		if (this.publicKey == null) {
			throw new Error("Attestation service has not been initialized!");
		}

		return this.publicKey;
	}

	/**
	 * Verifies TEE application integrity through RTMR3 measurement validation and application identity enforcement.
	 *
	 * This method implements the DStack attestation approach by:
	 * 1. Parsing and validating the TEE event log to ensure integrity of RTMR3 events
	 * 2. Extracting application information (app ID, compose hash, instance ID) from validated events
	 * 3. Replaying RTMR3 calculation from validated event digests
	 * 4. Comparing replayed RTMR3 against the value reported by the TEE hardware
	 * 5. Validating that the application ID matches the expected authorized application
	 *
	 * The validation approach follows the reference implementation provided by the Dstack authors
	 * https://github.com/Dstack-TEE/dstack-examples/blob/main/attestation/rtmr3-based/verify.py
	 *
	 * Only RTMR3 events are validated, as other IMR registers may have different validation rules.
	 * Event validation uses the format: sha384(event_type:event_name:event_payload)
	 *
	 * Any modification to application components or tampering with event logs will result in
	 * validation failures, preventing execution of unauthorized or tampered applications.
	 *
	 * @param eventLogJson - JSON string containing TEE event log with component measurements
	 * @param reportedRtmr3 - RTMR3 value reported by TEE hardware for comparison
	 * @returns Promise that resolves if integrity validation passes
	 * @throws {Error} When event log JSON is malformed or contains invalid RTMR3 events
	 * @throws {Error} When replayed RTMR3 doesn't match reported value (indicates tampering)
	 * @throws {Error} When application ID doesn't match expected authorized application
	 * @throws {Error} When required application events are missing from the log
	 */
	public async verifyTEEApplicationIntegrity(
		eventLogJson: string,
		reportedRtmr3: string,
	): Promise<void> {
		const eventLog = this.parseEventLog(eventLogJson);
		await this.validateAllEvents(eventLog);
		const appInfo = this.extractApplicationInfo(eventLog);
		const replayedRtmr3 = await this.replayRtmr3(eventLog);

		if (replayedRtmr3 !== reportedRtmr3) {
			throw new Error(
				`RTMR3 mismatch: replayed ${replayedRtmr3} != reported ${reportedRtmr3}`,
			);
		}

		this.validateApplicationInfo(appInfo);
	}

	/**
	 * Verifies that the TEE attestation report cryptographically commits to the provided public key
	 * and validates the attestation's freshness.
	 *
	 * This method establishes the critical link between the TEE hardware attestation and the
	 * TEE's public key, which if left unchecked, may otherwise be modified by the system Relay.
	 * It does so by:
	 * 1. Validating attestation freshness (timestamp must be within 5 minutes)
	 * 2. Extracting the reportData field from the TEE attestation
	 * 3. Reconstructing the expected reportData by hashing 'app-data:' prefix + JSON-encoded attested data (publicKey + timestamp)
	 * 4. Comparing the reconstructed hash with the TEE-reported hash byte-by-byte
	 *
	 * The TEE reportData field contains a SHA-512 hash that was generated inside the TEE,
	 * proving that the TEE had access to both the public key and timestamp during attestation generation.
	 * This prevents key substitution attacks where a malicious Relay might try to use a different
	 * public key than the one actually protected by the TEE, and prevents replay attacks using stale attestations.
	 *
	 * @param reportData - Hexadecimal TEE reportData containing hash of attested public key and timestamp
	 * @param publicKey - Base64-encoded public key that should be attested by the TEE
	 * @param timestamp - Unix timestamp (milliseconds) when the attestation was generated
	 * @returns Promise that resolves if the key has been cryptographically verified by the TEE and attestation is fresh
	 * @throws {Error} When attestation timestamp is older than 5 minutes (prevents replay attacks)
	 * @throws {Error} When cryptographic hash calculation fails
	 * @throws {Error} When reportData length is invalid (not 64 bytes for SHA-512)
	 */
	public async verifyTEEPublicKey(
		reportData: string,
		publicKey: string,
		timestamp: number,
	): Promise<void> {
		if (Date.now() - timestamp > TEE_REPORT_EXPIRY_MS) {
			this.logger.error(
				`TEE attestation timestamp is too old (older than ${TEE_REPORT_EXPIRY_MS / (60 * 1000)} minutes)`,
			);
			throw new Error(
				`TEE attestation timestamp is too old (older than ${TEE_REPORT_EXPIRY_MS / (60 * 1000)} minutes)`,
			);
		}

		const publicKeyIsAttested = await this.verifyReportAttestsPublicKey(
			reportData,
			publicKey,
			timestamp,
		);

		if (!publicKeyIsAttested) {
			throw new Error(
				"TEE reported public key does not match attestation report",
			);
		}
	}

	private async verifyReportAttestsPublicKey(
		reportData: string,
		publicKey: string,
		timestamp: number,
	): Promise<boolean> {
		try {
			this.logger.info("Verifying report attests public key");
			const reportDataHash = decodeBytes(reportData, "hex");
			if (reportDataHash.length !== SHA512_HASH_LENGTH) {
				this.logger.error(
					`Invalid report data hash length: ${reportDataHash.length}`,
				);
				return false;
			}

			const encoder = new TextEncoder();
			const prefixBytes = encoder.encode(TEE_REPORT_DATA_PREFIX);
			const attestedData = encoder.encode(
				JSON.stringify({
					publicKey,
					timestamp,
				}),
			);

			const reconstructedReportData = new Uint8Array(
				prefixBytes.length + attestedData.length,
			);
			reconstructedReportData.set(prefixBytes, 0);
			reconstructedReportData.set(attestedData, prefixBytes.length);

			const hash = await crypto.subtle.digest(
				TEE_REPORT_DATA_HASH,
				reconstructedReportData,
			);
			const hashView = new Uint8Array(hash);
			return hashView.every((byte, i) => byte === reportDataHash[i]);
		} catch (_error) {
			return false;
		}
	}

	private parseEventLog(eventLogJson: string): EventLogEntry[] {
		try {
			const parsedLog = JSON.parse(eventLogJson);
			return EventLogSchema.parse(parsedLog);
		} catch (error) {
			throw new Error(`Failed to parse event log JSON: ${error}`);
		}
	}

	/**
	 * Validates all events in the event log by verifying their digest values.
	 *
	 * Following the Phala team's reference implementation, only RTMR3 events are validated
	 * as other IMR registers may have different validation requirements.
	 *
	 * @param eventLog - Array of event log entries to validate
	 * @throws {Error} When any RTMR3 event has an invalid digest
	 */
	private async validateAllEvents(eventLog: EventLogEntry[]): Promise<void> {
		for (const event of eventLog) {
			const isValid = await this.validateEvent(event);
			if (!isValid) {
				throw new Error(
					`Invalid event digest found for event: ${event.event} in IMR ${event.imr}`,
				);
			}
		}
	}

	/**
	 * Validates an individual event's digest according to the DStack format.
	 *
	 * Implementation based on the Phala team's reference code:
	 * https://github.com/Dstack-TEE/dstack-examples/blob/main/attestation/rtmr3-based/verify.py
	 *
	 * Only validates IMR3 events - other events are skipped as they may have different
	 * validation requirements. For RTMR3 events, calculates:
	 * sha384(event_type:event_name:event_payload) and compares to stored digest.
	 *
	 * Newer dstack images omit the digest for IMR3 events (empty string) and only
	 * ship the event payload. Such events are accepted here because the RTMR3
	 * replay (built from the recomputed digests) is still checked against the
	 * hardware-signed quote, which is what binds the event log to the TEE.
	 *
	 * @param event - Event log entry to validate
	 * @returns Promise resolving to true if the event is valid, false otherwise
	 */
	private async validateEvent(event: EventLogEntry): Promise<boolean> {
		try {
			// Skip validation for non-IMR3 events, following Phala team's reference implementation
			if (event.imr !== RTMR3_IMR_INDEX) {
				return true;
			}

			const calculatedDigest = await this.computeEventDigest(event);
			return event.digest === "" || calculatedDigest === event.digest;
		} catch (_error) {
			return false;
		}
	}

	/**
	 * Computes the DStack digest of an event log entry:
	 * sha384(event_type:event_name:event_payload), with event_type encoded as a
	 * 4-byte little-endian integer.
	 *
	 * @param event - Event log entry to hash
	 * @returns Promise resolving to the hex-encoded SHA-384 digest
	 * @throws {Error} When the event name is empty
	 */
	private async computeEventDigest(event: EventLogEntry): Promise<string> {
		if (!event.event || event.event.trim().length === 0) {
			throw new Error("Event name must not be empty");
		}

		// Convert event payload from hex to bytes for validation
		let eventPayloadBytes: Uint8Array;
		try {
			eventPayloadBytes = decodeBytes(event.event_payload, "hex");
		} catch {
			// If hex decoding fails, treat as UTF-8 string (for empty payloads or plain text events)
			eventPayloadBytes = new TextEncoder().encode(event.event_payload);
		}

		// Build the validation string: event_type:event_name:event_payload
		// Event type is encoded as 4-byte little-endian integer (matching DStack reference implementation)
		const eventTypeBytes = new Uint8Array(4);
		eventTypeBytes[0] = event.event_type & 0xff; // LSB (least significant byte)
		eventTypeBytes[1] = (event.event_type >> 8) & 0xff;
		eventTypeBytes[2] = (event.event_type >> 16) & 0xff;
		eventTypeBytes[3] = (event.event_type >> 24) & 0xff; // MSB (most significant byte)

		const colonBytes = new TextEncoder().encode(":");
		const eventNameBytes = new TextEncoder().encode(event.event);

		const totalLength =
			eventTypeBytes.length +
			colonBytes.length +
			eventNameBytes.length +
			colonBytes.length +
			eventPayloadBytes.length;

		const buffer = new Uint8Array(totalLength);

		let offset = 0;
		buffer.set(eventTypeBytes, offset);
		offset += eventTypeBytes.length;
		buffer.set(colonBytes, offset);
		offset += colonBytes.length;
		buffer.set(eventNameBytes, offset);
		offset += eventNameBytes.length;
		buffer.set(colonBytes, offset);
		offset += colonBytes.length;
		buffer.set(eventPayloadBytes, offset);

		const calculatedHash = await crypto.subtle.digest(
			RTMR3_HASH_ALGORITHM,
			buffer,
		);
		return encodeBytes(new Uint8Array(calculatedHash), "hex");
	}

	/**
	 * Extracts application information from validated event log entries.
	 *
	 * Looks for specific events (app-id, compose-hash, instance-id, key-provider)
	 * and extracts their payload values for application validation.
	 *
	 * @param eventLog - Array of validated event log entries
	 * @returns Object containing extracted application information
	 * @throws {Error} When required application events are missing
	 */
	private extractApplicationInfo(eventLog: EventLogEntry[]): ApplicationInfo {
		const appInfo: Partial<ApplicationInfo> = {};

		for (const event of eventLog) {
			switch (event.event) {
				case EVENT_NAMES.APP_ID:
					appInfo.app_id = event.event_payload;
					break;
				case EVENT_NAMES.COMPOSE_HASH:
					appInfo.compose_hash = event.event_payload;
					break;
				case EVENT_NAMES.INSTANCE_ID:
					appInfo.instance_id = event.event_payload;
					break;
				case EVENT_NAMES.KEY_PROVIDER:
					try {
						// Key provider might be stored as hex-encoded string
						const keyProviderBytes = decodeBytes(event.event_payload, "hex");
						const keyProviderJson = new TextDecoder().decode(keyProviderBytes);
						appInfo.key_provider = JSON.parse(keyProviderJson);
					} catch {
						// If not hex, treat as regular string and parse JSON
						try {
							appInfo.key_provider = JSON.parse(event.event_payload);
						} catch {
							throw new Error(
								`Invalid key_provider JSON format: ${event.event_payload}`,
							);
						}
					}

					try {
						appInfo.key_provider = KeyProviderSchema.parse(
							appInfo.key_provider,
						);
					} catch (schemaError) {
						if (schemaError instanceof z.ZodError) {
							throw new Error(
								`Invalid key_provider: ${schemaError.issues.map((issue) => `${issue.path.join(".")}: ${issue.message}`).join(", ")}`,
							);
						}
						throw schemaError;
					}
					break;
			}
		}

		try {
			return ApplicationInfoSchema.parse(appInfo);
		} catch (error) {
			throw new Error(`Missing required application events in log: ${error}`);
		}
	}

	/**
	 * Validates that the extracted application information matches expected values.
	 *
	 * @param appInfo - Application information extracted from event log
	 * @throws {Error} When application ID doesn't match expected value
	 */
	private validateApplicationInfo(appInfo: ApplicationInfo): void {
		const matches = this.expectedAppIds.some(
			(id) => id.toLowerCase() === appInfo.app_id.toLowerCase(),
		);
		if (!matches) {
			throw new Error(
				`Invalid app ID: expected one of [${this.expectedAppIds.join(", ")}], got ${appInfo.app_id}`,
			);
		}
	}

	/**
	 * Replays RTMR3 calculation from validated event log entries.
	 *
	 * Filters events for IMR index 3 and replays the RTMR calculation using the
	 * digest recomputed from each event, following the DStack approach. Using the
	 * recomputed digest (rather than the one shipped in the log) keeps the replay
	 * independent of whether the TEE image populates the digest field.
	 *
	 * @param eventLog - Array of validated event log entries
	 * @returns Promise resolving to the calculated RTMR3 value as a hexadecimal string
	 */
	private async replayRtmr3(eventLog: EventLogEntry[]): Promise<string> {
		const rtmr3Events = eventLog.filter(
			(event) => event.imr === RTMR3_IMR_INDEX,
		);
		const digestHistory = await Promise.all(
			rtmr3Events.map((event) => this.computeEventDigest(event)),
		);

		return await this.replayRtmrHistory(digestHistory);
	}

	/**
	 * Replay the RTMR history to calculate the final RTMR value.
	 *
	 * Implementation based on the Phala team's reference code:
	 * https://github.com/Dstack-TEE/dstack-examples/blob/main/attestation/rtmr3-based/verify.py
	 *
	 * Uses SHA-384 to iteratively hash the measurement register with each digest:
	 * mr = sha384(mr + digest) for each digest in history
	 *
	 * @param history - List of digest values to replay
	 * @returns Promise resolving to the calculated RTMR value as a hexadecimal string
	 */
	private async replayRtmrHistory(history: string[]): Promise<string> {
		if (history.length === 0) {
			return INIT_MR;
		}

		let mr = decodeBytes(INIT_MR, "hex");

		for (const digest of history) {
			let digestBytes = decodeBytes(digest, "hex");

			// Pad digest to 48 bytes (SHA-384 length) if shorter
			if (digestBytes.length < SHA384_HASH_LENGTH) {
				const paddedBytes = new Uint8Array(SHA384_HASH_LENGTH);
				paddedBytes.set(digestBytes, 0);
				digestBytes = paddedBytes;
			}

			// Calculate mr = sha384(mr + digest)
			const combined = new Uint8Array(mr.length + digestBytes.length);
			combined.set(mr, 0);
			combined.set(digestBytes, mr.length);

			const hashBuffer = await crypto.subtle.digest(
				RTMR3_HASH_ALGORITHM,
				combined,
			);
			mr = new Uint8Array(hashBuffer);
		}

		return encodeBytes(mr, "hex");
	}

	public async safeDebugState(): Promise<Record<string, unknown>> {
		let teePublicKey: {
			bytes: string;
			encoding: string;
			keyType: string;
		} | null = null;

		if (this.publicKey != null) {
			try {
				const rawKey = await crypto.subtle.exportKey("raw", this.publicKey);
				teePublicKey = {
					bytes: encodeBytes(new Uint8Array(rawKey), "base64"),
					encoding: "base64",
					keyType: "p256",
				};
			} catch {
				teePublicKey = null;
			}
		}

		return {
			teePublicKey,
			expectedAppIds: this.expectedAppIds,
		};
	}
}
